/**
 * A word-level diff between a version and the one after it (earlier versions, Arthur, 19 October 2026): what the
 * next version removed and what it added, word by word, with the spacing kept. A leaf module, tested in
 * web/test/word-diff.test.ts.
 *
 * Longest common subsequence over words. A pair of texts too long for that to be cheap (over LIMIT word pairs)
 * is answered as one removal and one addition, so a pathological note can never stall the page.
 */
export type DiffPart = { kind: 'same' | 'removed' | 'added'; text: string };

const LIMIT = 400_000;

/** Words with the whitespace after each kept on it, so joining the parts gives the text back exactly. */
function tokens(s: string): string[] {
  return s.match(/\S+\s*|\s+/g) ?? [];
}

export function wordDiff(before: string, after: string): DiffPart[] {
  const a = tokens(before), b = tokens(after);
  const key = (t: string) => t.trimEnd();
  if (a.length * b.length > LIMIT) {
    return [...(before ? [{ kind: 'removed' as const, text: before }] : []), ...(after ? [{ kind: 'added' as const, text: after }] : [])];
  }
  // lcs[i][j]: the longest common run of a[i..] and b[j..], compared word by word, spacing ignored.
  const lcs: Uint32Array[] = Array.from({ length: a.length + 1 }, () => new Uint32Array(b.length + 1));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i]![j] = key(a[i]!) === key(b[j]!) ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }
  const out: DiffPart[] = [];
  const push = (kind: DiffPart['kind'], text: string) => {
    const last = out[out.length - 1];
    if (last && last.kind === kind) last.text += text; else out.push({ kind, text });
  };
  let i = 0, j = 0;
  while (i < a.length && j < b.length) {
    if (key(a[i]!) === key(b[j]!)) { push('same', b[j]!); i++; j++; }
    else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) { push('removed', a[i]!); i++; }
    else { push('added', b[j]!); j++; }
  }
  while (i < a.length) push('removed', a[i++]!);
  while (j < b.length) push('added', b[j++]!);
  return out;
}
